import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  SubscriptionPlan,
  ISubscriptionPlanPagination,
  SubscriptionPlanFeature,
} from './@types';
import AuthServices from './auth.api';
import { getAuthToken } from '../utils/getAuthToken';

class SubscriptionPlanService {
  private api: AxiosInstance;

  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          AuthServices.logout();
        }
        return Promise.reject(error);
      },
    );
  }

  public async getSubscriptionPlans(
    queryParams?: Record<string, any>,
  ): Promise<ISubscriptionPlanPagination> {
    const response = await this.api.get('/subscription-plans', { params: queryParams });
    // API may return array directly or paginated object
    if (Array.isArray(response.data)) {
      return {
        results: response.data,
        page: 1,
        limit: response.data.length,
        totalPages: 1,
        totalResults: response.data.length,
      };
    }
    return response.data;
  }

  public async getSubscriptionPlan(id: string): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.get(
      `/subscription-plans/${id}`,
    );
    return response.data;
  }

  public async createSubscriptionPlan(
    plan: Partial<SubscriptionPlan>,
  ): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.post(
      '/subscription-plans',
      plan,
    );
    return response.data;
  }

  public async updateSubscriptionPlan(
    id: string,
    plan: Partial<SubscriptionPlan>,
  ): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.patch(
      `/subscription-plans/${id}`,
      plan,
    );
    return response.data;
  }

  public async createFeature(
    feature: Partial<SubscriptionPlanFeature>,
  ): Promise<SubscriptionPlanFeature> {
    const response: AxiosResponse<SubscriptionPlanFeature> =
      await this.api.post('/subscription-plans/features', feature);
    return response.data;
  }

  public async getFeature(id: string): Promise<SubscriptionPlanFeature> {
    const response: AxiosResponse<SubscriptionPlanFeature> =
      await this.api.get(`/subscription-plans/features/${id}`);
    return response.data;
  }

  public async updateFeature(
    id: string,
    feature: Partial<SubscriptionPlanFeature>,
  ): Promise<SubscriptionPlanFeature> {
    const response: AxiosResponse<SubscriptionPlanFeature> =
      await this.api.patch(`/subscription-plans/features/${id}`, feature);
    return response.data;
  }
}

const _subscriptionPlanService = new SubscriptionPlanService(
  process.env.REACT_APP_SERVER_API_URL || 'http://localhost:3000',
);

export default _subscriptionPlanService;
